import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import {
  getSampleTasksPath,
  sampleTaskFeedbackCountPath,
} from '../../../shared/misc-utilities/sampling-portal-paths';
import { SampleTask } from '../../../team-apis/otterdam/sample-task';
import { TaskFeedbackCountResponse } from '../../../team-apis/otterdam/task-feedback-count-response';
import { SamplingWorkspaceStore } from '../../sampling-workspace/state/sampling-workspace-store';
import { FeatureFlagService } from '../../shared/misc-services/feature-flag.service';
import { AbstractResource } from '../abstract-resource';

@Injectable()
export class SampleTaskLoadingService extends AbstractResource {
  private samplingWorkspaceStore = inject(SamplingWorkspaceStore);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private featureFlagService = inject(FeatureFlagService);

  async load() {
    if (!this.featureFlagService.hasFlag('new-sampling-nav')) {
      return;
    }

    const [sampleTasks, { taskCounts }] = await Promise.all([
      this.loadSampleTasksStatus(),
      this.loadTaskFeedbackCount(),
    ]);
    const sampleTaskWithFeedbackCount = sampleTasks.map((sampleTask) => ({
      ...sampleTask,
      feedbackCount: taskCounts[sampleTask.task_id] ?? 0,
    }));

    this.samplingWorkspaceStore.assign({
      sampleTasks: sampleTaskWithFeedbackCount,
    });
  }

  private async loadSampleTasksStatus() {
    const { projectId, sampleId } = this.workspaceConfigService;

    return lastValueFrom(
      this.request$<SampleTask[]>(
        'GET',
        getSampleTasksPath(projectId, sampleId!),
        {
          tokenScope: 'access_token',
          customErrorMessage: 'Failed to load sample tasks.',
        },
      ),
    );
  }

  private async loadTaskFeedbackCount() {
    const { projectId, sampleId } = this.workspaceConfigService;

    return lastValueFrom(
      this.request$<TaskFeedbackCountResponse>(
        'GET',
        sampleTaskFeedbackCountPath(projectId, sampleId!),
        {
          tokenScope: 'access_token',
          customErrorMessage: 'Failed to load sample task feedback counts.',
        },
      ),
    );
  }
}
